/**
 * Shippo webhook event contracts — parsed envelope at the route boundary
 * (`app/api/webhooks/shippo/route.ts`).
 *
 * Sibling-F · F8. Before this schema, the webhook handler accepted any
 * `{ event?: string; data?: unknown }` shape and cast `data` to a local
 * loose `ShippoTransactionEvent = { object_id?, status?, ... }` interface.
 * The same opaque-wrapper class as `CaptureOrderRequestSchema.checkoutData`
 * before PR-B `42d524eb` tightened it. F8 closes that class on the webhook
 * seam.
 *
 * These schemas pin EXACTLY the snake_case wire surface our handler reads —
 * not Shippo's full webhook menu. Shippo emits raw JSON over HTTP (the SDK
 * camelCase mapping does NOT happen on the webhook path), so the wire is
 * the snake_case shape documented at https://docs.goshippo.com/docs/webhooks.
 *
 * Strategy:
 *   - The envelope is a `z.discriminatedUnion("event", [...])` so the
 *     parser branches structurally on the event kind. `data` is typed
 *     per-branch — `transaction_*` carries a Transaction shape,
 *     `track_updated` carries a Track shape. No `Record<string, unknown>`
 *     leaks past the parse.
 *   - `status` (transaction kind) is enum-narrowed to the four values
 *     Shippo documents (`SUCCESS`/`ERROR`/`QUEUED`/`WAITING`). Anything
 *     else fails parse at the boundary, becomes a warn-log, and surfaces
 *     as a 400 — instead of silently being mapped to `'purchasing'` by
 *     `decideTransition`'s defensive default. If Shippo adds a new
 *     status, extend the enum here in the same diff that handles it.
 *   - `tracking_status.status` (track kind) is enum-narrowed to the six
 *     values mirrored in `TRACKING_STATUS_MAP` (`lib/shippo/client.ts`).
 *
 * @see app/api/webhooks/shippo/route.ts — the only caller of these schemas
 * @see lib/shippo/webhook-transition.ts — pure FSM downstream of the parse
 * @see .tondocs/RELIABILITY_ROADMAP.md §3a Sibling-F · F8
 * @see .tondocs/shippo/HANDOFF_RELIABILITY_GAPS_2026_06_27.md PR-H
 * @see .tondocs/skills/type-driven-design.md §5 — Parse, Don't Validate
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Transaction event — `transaction_created` + `transaction_updated`
// ---------------------------------------------------------------------------

/**
 * Shippo's documented `transaction.status` values on the webhook wire.
 * The handler maps these through `decideTransition` (`lib/shippo/
 * webhook-transition.ts`) — SUCCESS becomes `'purchased'`, ERROR becomes
 * `'failed'`, QUEUED/WAITING become `'purchasing'`. Anything outside this
 * set fails the boundary parse — the previous `string & {}` widening that
 * silently mapped unknown values to `'purchasing'` is intentionally
 * removed (Sibling-F lesson: silent widening at the boundary == drift seed).
 */
export const SHIPPO_TRANSACTION_WEBHOOK_STATUSES = [
  "SUCCESS",
  "ERROR",
  "QUEUED",
  "WAITING",
] as const;
export type ShippoTransactionWebhookStatus =
  (typeof SHIPPO_TRANSACTION_WEBHOOK_STATUSES)[number];

/**
 * Parsed `data` payload for `transaction_created` / `transaction_updated`
 * webhook events. Pinned to EXACTLY the fields the handler reads — Shippo
 * additionally sends `rate`, `messages`, `commercial_invoice_url`, `eta`,
 * `parcel`, … but those are not consumed by the route, so they are stripped
 * by Zod's default object-strip semantics.
 *
 * `metadata` is the seller_order id we set at label-buy
 * (`lib/shippo/labels.ts::purchaseLabelForOrder` line 102). Shippo round-
 * trips it on every transaction event.
 */
export interface ShippoTransactionEventData {
  object_id: string;
  status: ShippoTransactionWebhookStatus;
  label_url?: string | null;
  tracking_number?: string | null;
  metadata?: string | null;
}

export const ShippoTransactionEventDataSchema =
  pinned<ShippoTransactionEventData>()(
    z.object({
      object_id: z.string().min(1),
      status: z.enum(SHIPPO_TRANSACTION_WEBHOOK_STATUSES),
      label_url: z.string().nullish(),
      tracking_number: z.string().nullish(),
      metadata: z.string().nullish(),
    }),
  );

// ---------------------------------------------------------------------------
// Track event — `track_updated`
// ---------------------------------------------------------------------------

/**
 * Mirror of Shippo's SCREAMING_SNAKE tracking statuses. The cron at
 * `app/api/cron/shippo-reconciliation/route.ts` (via
 * `lib/shippo/client.ts::TRACKING_STATUS_MAP`) maps these to the lowercase
 * `seller_orders.tracking_status` CHECK constraint enum. The webhook now
 * mirrors that mapping inline so a `track_updated` arrival writes the same
 * column as a cron-reconciliation sweep.
 */
export const SHIPPO_TRACK_WEBHOOK_STATUSES = [
  "PRE_TRANSIT",
  "TRANSIT",
  "DELIVERED",
  "RETURNED",
  "FAILURE",
  "UNKNOWN",
] as const;
export type ShippoTrackWebhookStatus =
  (typeof SHIPPO_TRACK_WEBHOOK_STATUSES)[number];

/**
 * Nested `tracking_status` object on the `track_updated` payload. F-07
 * (column `tracking_failure_reason` landed 2026-06-24, commit `590f1a8d`)
 * is sourced from `status_details` when `status === "FAILURE"` —
 * previously the only end-to-end writer was the canonical builder
 * (`lib/shipping/build-label-row.ts::buildTrackingFragment`); the webhook
 * read-side wire is added in PR-H.
 */
export interface ShippoTrackEventTrackingStatus {
  status: ShippoTrackWebhookStatus;
  /** Free-text rejection reason from the carrier. Source for F-07 column. */
  status_details?: string | null;
  /** ISO-8601 wire string (NOT a JS Date — the SDK camelCases on the read
   *  path; the raw webhook delivers the string verbatim). */
  status_date?: string | null;
}

export const ShippoTrackEventTrackingStatusSchema =
  pinned<ShippoTrackEventTrackingStatus>()(
    z.object({
      status: z.enum(SHIPPO_TRACK_WEBHOOK_STATUSES),
      status_details: z.string().nullish(),
      status_date: z.string().nullish(),
    }),
  );

/**
 * Parsed `data` payload for `track_updated`. `tracking_number` is the
 * natural key — Shippo guarantees it on every tracking event, and our
 * `seller_orders` row already carries it (set at label-buy by the
 * canonical builder). `metadata` is best-effort — Shippo forwards it
 * from the originating Transaction when available, but tracking webhooks
 * can also fire from carrier scans without it.
 */
export interface ShippoTrackEventData {
  tracking_number: string;
  carrier?: string | null;
  tracking_status: ShippoTrackEventTrackingStatus;
  metadata?: string | null;
}

export const ShippoTrackEventDataSchema = pinned<ShippoTrackEventData>()(
  z.object({
    tracking_number: z.string().min(1),
    carrier: z.string().nullish(),
    tracking_status: ShippoTrackEventTrackingStatusSchema,
    metadata: z.string().nullish(),
  }),
);

// ---------------------------------------------------------------------------
// Discriminated envelope — `event` decides the shape of `data`
// ---------------------------------------------------------------------------

/**
 * Discriminated webhook envelope. Adding a new event handler in
 * `route.ts` MUST extend this union (and a `default: unreachable(x: never)`
 * probe in the route catches the omission at compile-time).
 *
 * Events Shippo emits that we DO NOT handle (`batch_*`, `carrier_account_*`,
 * `manifest_*`, …) fall through the early-exit ignored-event branch in the
 * route BEFORE this parse runs — so an unknown event surfaces as a
 * structured `shippo.webhook_ignored` info-log + 200, not a 400.
 */
export type ShippoWebhookEvent =
  | { event: "transaction_created"; data: ShippoTransactionEventData }
  | { event: "transaction_updated"; data: ShippoTransactionEventData }
  | { event: "track_updated"; data: ShippoTrackEventData };

export const ShippoWebhookEventSchema = pinned<ShippoWebhookEvent>()(
  z.discriminatedUnion("event", [
    z.object({
      event: z.literal("transaction_created"),
      data: ShippoTransactionEventDataSchema,
    }),
    z.object({
      event: z.literal("transaction_updated"),
      data: ShippoTransactionEventDataSchema,
    }),
    z.object({
      event: z.literal("track_updated"),
      data: ShippoTrackEventDataSchema,
    }),
  ]),
);

/**
 * The set of `event` strings the handler dispatches on. Used by the route's
 * pre-parse early-exit (so unknown Shippo events do not look like parse
 * failures — they are intentionally ignored). Keep in lockstep with
 * `ShippoWebhookEvent`'s discriminator literals.
 */
export const SHIPPO_HANDLED_WEBHOOK_EVENTS = [
  "transaction_created",
  "transaction_updated",
  "track_updated",
] as const;
export type ShippoHandledWebhookEvent =
  (typeof SHIPPO_HANDLED_WEBHOOK_EVENTS)[number];
